/**
 * ClinicStaffingBar — horizontal bar visual of every clinic's staffing %.
 * Green/yellow/red per Thrive's own threshold table. Sorted lowest first
 * so critical clinics rise to the top for the leadership review.
 */
import React from 'react';
import { Card } from '../ui/card';
import { Link } from 'react-router-dom';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4', plum: '#4A3B5F', gold: '#B08A3A',
  ok: '#3B6F5D', yellow: '#B08A3A', red: '#B54B4B',
};

const COLOR_BY = { green: C.ok, yellow: C.yellow, red: C.red };

export default function ClinicStaffingBar({ rows }) {
  if (!rows?.length) {
    return (
      <Card className="p-6 border text-center text-sm" style={{ borderColor: C.border, color: C.soft }}>
        Loading clinic snapshot…
      </Card>
    );
  }
  // rows come pre-sorted lowest → highest from /api/hr/staffing.
  const max = 100;
  return (
    <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-clinic-bar-visual">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.gold }}>
          Clinic Staffing · Snapshot
        </div>
        <div className="text-[11px]" style={{ color: C.soft }}>
          Green ≥100% · Yellow 75-99% · Red &lt;75%
        </div>
      </div>
      <div className="space-y-1.5">
        {rows.map(r => {
          const pct = Math.min(max, r.core_pct || 0);
          const color = COLOR_BY[r.status?.color] || C.plum;
          return (
            <Link key={r.clinic} to={`/hr/staffing?clinic=${r.clinic}`}
                  className="flex items-center gap-3 group"
                  data-testid={`hr-clinic-bar-${r.clinic}`}>
              <div className="w-32 text-sm truncate" style={{ color: C.ink }}>
                {r.clinic}
                {r.status?.od_vacant && (
                  <span className="ml-1 text-[9px] font-bold" style={{ color: C.red }}>· OD MISSING</span>
                )}
              </div>
              <div className="flex-1 h-6 rounded relative overflow-hidden" style={{ background: C.paper }}>
                <div className="h-full rounded transition-all group-hover:opacity-90"
                     style={{ width: `${pct}%`, background: color }} />
                <div className="absolute inset-0 flex items-center px-3 text-[11px] font-semibold"
                     style={{ color: pct > 45 ? '#fff' : C.ink }}>
                  {r.core_pct}% · {r.core_filled}/{r.core_target}
                  {r.open_positions > 0 && (
                    <span className="ml-auto opacity-90">{r.open_positions} to hire</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
